import { log } from '@/lib/log'

/** The languages the panel is published in. */
export const LOCALES = ['en', 'fr'] as const

export type Locale = (typeof LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'en'

/** Each language named in itself. Never translated — a reader looking for their own language
 * is scanning for the word they already know. */
export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  fr: 'Français',
}

const STORAGE_KEY = 'vecos-admin-locale'

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value)
}

/** The stored choice, or `null` if there is none. */
export function readStoredLocale(): Locale | null {
  // A browser set to block site data throws on access rather than returning null.
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (isLocale(stored)) return stored
  } catch (cause) {
    log.exception('Could not read the stored language', cause)
  }

  return null
}

export function storeLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch (cause) {
    log.exception('Could not store the language preference', cause)
  }
}

/**
 * What to show somebody who has never chosen.
 *
 * An explicit choice always wins. Failing that the browser decides, matched on the language
 * subtag alone so `fr-CA` and `fr-BE` both land on French rather than falling back to English
 * over a region nobody configured deliberately.
 */
export function resolveInitialLocale(
  languages: readonly string[] = navigator.languages,
): Locale {
  const stored = readStoredLocale()
  if (stored) return stored

  for (const tag of languages) {
    const base = tag.toLowerCase().split('-')[0]
    if (isLocale(base)) return base
  }

  return DEFAULT_LOCALE
}
